/**
 * Error reporter — a thin wrapper that:
 *   1. Logs to the console in development (so you still see the stack).
 *   2. Posts to the project's own `/api/errors` ingestion endpoint so
 *      admins can see what's failing in production via the admin
 *      panel — no third-party dependency required.
 *   3. Posts to Sentry when `VITE_SENTRY_DSN` is configured AND the
 *      `@sentry/browser` package is installed.
 *
 * The `/api/errors` POST is fire-and-forget — never throws, never
 * blocks the UI. Even if the API is down or the network drops, the
 * caller code continues unaffected.
 *
 * Sentry is intentionally NOT a hard dependency — the app shouldn't
 * grow a 30 KB shim per route just to report errors. We only load the
 * SDK lazily when a DSN exists. The `beforeSend` scrubber drops fields
 * named `password`, `token`, `secret`, `email`, `phone`, `card`, `cvc`,
 * `ssn` (project-context §7.6.10).
 */

import { isChunkLikeError } from './lazy-with-retry'

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined
const ENV = import.meta.env.MODE
const API_URL = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')
const SCRUB_KEYS = [
  'password',
  'token',
  'secret',
  'email',
  'phone',
  'card',
  'cvc',
  'ssn',
  'authorization',
]

/**
 * Stable per-tab session id — lets us correlate multiple errors in the
 * same browsing session without storing PII. Survives page refreshes
 * (sessionStorage) but resets when the tab closes.
 */
function getSessionId(): string {
  if (typeof window === 'undefined') return 'ssr'
  try {
    let id = window.sessionStorage.getItem('__error_session_id')
    if (!id) {
      id = `s-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(
        36,
      )}`
      window.sessionStorage.setItem('__error_session_id', id)
    }
    return id
  } catch {
    return 'no-storage'
  }
}

/**
 * Tracks the current authenticated user id so it can attach to error
 * reports without needing the auth store as a hard dependency.
 */
let currentUserId: string | null = null

/**
 * Build a stable fingerprint for grouping duplicates in the admin
 * panel. Combines: error name + first frame of the stack + route id.
 */
function buildFingerprint(error: unknown, context?: ReportContext): string {
  const name = error instanceof Error ? error.name : 'Unknown'
  const message = error instanceof Error ? error.message : String(error)
  const firstStackLine =
    error instanceof Error && error.stack
      ? error.stack.split('\n')[1]?.trim().slice(0, 120) ?? ''
      : ''
  const route = (context?.route ?? '') as string
  // Truncate the message to a stable prefix so dynamic suffixes
  // (e.g. ids, dates) don't break grouping.
  const messagePrefix = message.slice(0, 80)
  return `${name}::${messagePrefix}::${firstStackLine}::${route}`
}

interface ReportContext {
  route?: string
  componentStack?: string
  [key: string]: unknown
}

/**
 * Minimal subset of the Sentry surface we use. Typed loosely so the
 * SDK can be optional at compile-time.
 */
interface SentryLike {
  init: (options: Record<string, unknown>) => void
  captureException: (error: unknown) => void
  captureMessage: (message: string) => void
  setUser: (user: { id: string } | null) => void
  withScope: (cb: (scope: SentryScope) => void) => void
}
interface SentryScope {
  setTag: (key: string, value: string) => void
  setContext: (key: string, value: Record<string, unknown>) => void
}

let sentryReady: Promise<SentryLike | null> | null = null

async function loadSentry(): Promise<SentryLike | null> {
  if (!DSN) return null
  if (sentryReady) return sentryReady
  // Resolve through `Function('return import(...)')` so neither
  // TypeScript nor Vite tries to verify the module at compile time.
  // Sentry is optional — when the package is not installed the catch
  // turns the failure into a silent no-op.
  const dynImport = new Function('s', 'return import(s)') as (
    spec: string,
  ) => Promise<unknown>

  sentryReady = dynImport('@sentry/browser')
    .then((mod) => {
      const Sentry = mod as SentryLike
      Sentry.init({
        dsn: DSN,
        environment: ENV,
        tracesSampleRate: 0,
        beforeSend(event: { request?: { headers?: Record<string, string> } }) {
          if (event.request?.headers) {
            for (const key of Object.keys(event.request.headers)) {
              if (SCRUB_KEYS.includes(key.toLowerCase())) {
                delete event.request.headers[key]
              }
            }
          }
          return event
        },
      })
      return Sentry
    })
    .catch(() => null)
  return sentryReady
}

/**
 * Best-effort POST to the project's own `/api/errors` ingest endpoint.
 * Never throws, never blocks. Failure to ingest is silent on purpose —
 * we don't want a broken error reporter to surface to the user as a
 * second error.
 */
function postToApi(error: unknown, context?: ReportContext): void {
  if (!API_URL) return
  if (typeof window === 'undefined') return

  const isError = error instanceof Error
  const message = isError ? error.message : String(error)
  if (!message) return

  const payload = {
    message,
    stack: isError ? error.stack : undefined,
    componentStack: context?.componentStack,
    errorName: isError ? error.name : 'Unknown',
    routeId: context?.route,
    url: window.location?.href,
    userAgent: navigator?.userAgent,
    fingerprint: buildFingerprint(error, context),
    severity: 'error',
    isChunkError:
      typeof message === 'string' &&
      (message.toLowerCase().includes('chunk') ||
        message.toLowerCase().includes('failed to fetch dynamically imported module')),
    sessionId: getSessionId(),
    userId: currentUserId,
  }

  try {
    // Prefer keepalive so the request survives a page navigation
    // (e.g. user clicked the "Reload" button on the error screen).
    fetch(`${API_URL}/errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Swallow — network errors here must never bubble.
    })
  } catch {
    // Synchronous throws (rare) — silent.
  }
}

/**
 * Public reporter — never throws. Synchronous fast path for the
 * console; async path for our DB ingest + Sentry so neither blocks
 * render.
 *
 * Stale chunk/deploy artifacts ("Failed to fetch dynamically imported
 * module", React #185 `.default` reads, HTML-as-JS MIME) are NOT posted
 * to the API — they resolve themselves with a reload (lazyWithRetry +
 * RouteErrorBoundary handle that) and previously flooded the admin
 * errors page with unactionable noise. Real bugs still go through.
 */
export function reportError(error: unknown, context?: ReportContext): void {
  if (ENV !== 'production') {
    console.error('[reportError]', error, context)
  }

  const message = error instanceof Error ? error.message : String(error)
  if (isChunkLikeError(message ?? '')) {
    if (ENV !== 'production') {
      console.info('[reportError] suppressed stale-chunk noise:', message)
    }
    return
  }

  // 1. Fire-and-forget post to our own /api/errors. Works in dev too
  //    so you can test the admin panel locally.
  postToApi(error, context)

  // 2. Optional Sentry path
  if (!DSN) return
  void loadSentry().then((Sentry) => {
    if (!Sentry) return
    Sentry.withScope((scope) => {
      if (context?.route) scope.setTag('route', context.route)
      if (context) {
        const safe: Record<string, unknown> = {}
        for (const [k, v] of Object.entries(context)) {
          if (SCRUB_KEYS.includes(k.toLowerCase())) continue
          safe[k] = v
        }
        scope.setContext('extra', safe)
      }
      if (error instanceof Error) {
        Sentry.captureException(error)
      } else {
        Sentry.captureMessage(String(error))
      }
    })
  })
}

/**
 * Attach a user identifier to all subsequent reports. ID-only, never
 * email — project-context §7.6.10. Used by both the local API ingest
 * and the optional Sentry pipeline.
 */
export function setReporterUser(userId: string | null): void {
  currentUserId = userId
  if (!DSN) return
  void loadSentry().then((Sentry) => {
    if (!Sentry) return
    Sentry.setUser(userId ? { id: userId } : null)
  })
}
